import React, { useState } from 'react';
import { Box, Paper, Typography, Button, Stack, CircularProgress } from '@mui/material';
import { Add } from '@mui/icons-material';
import GlobalPromptQuestionCard from './GlobalPromptQuestionCard';
import GlobalPromptQuestionsEdit from './GlobalPromptQuestionsEdit';
import QuestionTipsCard from '../campaigns/CampaignQuestionsEditor/QuestionTipsCard';
import useFetchGlobalPromptQuestions from '@/hooks/useFetchGlobalPromptQuestions';
import useSaveGlobalPromptQuestions from './hooks/useSaveGlobalPromptQuestions';
import type { GlobalPromptQuestion, SaveGlobalQuestion } from './types';
import ErrorState from '../common/ErrorState';

const GlobalPromptQuestions: React.FC = () => {
    const [isEditing, setIsEditing] = useState(false);
    const { questions, isLoading, isError: isGlobalQuestionsError, errorMessage, refetch } = useFetchGlobalPromptQuestions();
    const { mutateAsync: saveQuestions, isPending: isSaving } = useSaveGlobalPromptQuestions();

    const handleSave = async (updated: SaveGlobalQuestion[]) => {
        try {
            await saveQuestions(updated);
            setIsEditing(false);
        } catch {
            // error already handled by useSaveGlobalPromptQuestions onError
        }
    };

    if (isGlobalQuestionsError) {
        return (
            <ErrorState
                title="Error loading global prompt questions"
                message={errorMessage || 'Service temporarily unavailable. Please try again.'}
                onRetry={() => refetch()}
            />
        );
    }

    const initialQuestions: GlobalPromptQuestion[] = questions.map((q, i) => ({ ...q, index: i }));

    return (
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', lg: 'row' } }}>
            <Box sx={{ flex: 1 }}>
                {isEditing ? (
                    <GlobalPromptQuestionsEdit
                        initialQuestions={initialQuestions}
                        onSave={handleSave}
                        onCancel={() => setIsEditing(false)}
                        isSaving={isSaving}
                    />
                ) : (
                    <Paper sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h3">Global questions</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {questions.length > 0 && (
                                    <Button variant="outlined" size="small" onClick={() => setIsEditing(true)}>
                                        Edit
                                    </Button>
                                )}
                                {questions.length === 0 && (
                                    <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => setIsEditing(true)}>
                                        Add question
                                    </Button>
                                )}
                            </Box>
                        </Box>

                        {isLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : questions.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No questions have been added yet. Add questions to get started.
                            </Typography>
                        ) : (
                            <Stack spacing={1} sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                {questions.map((q, i) => (
                                    <GlobalPromptQuestionCard key={q.id} question={q} number={i + 1} />
                                ))}
                            </Stack>
                        )}
                    </Paper>
                )}
            </Box>

            <Box sx={{ width: { xs: '100%', lg: '260px' }, flexShrink: 0 }}>
                <QuestionTipsCard viewMode={!isEditing} />
            </Box>
        </Box>
    );
};

export default GlobalPromptQuestions;
